import { t as localeText } from "@/shared/lib/translate";
import {
  Fragment,
  forwardRef,
  type AriaAttributes,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { CaretDownIcon } from "./icons";
import { cn } from "@/shared/lib/cn";
import { Button } from "./Button";
import { Popover } from "./Popover";
import { Text } from "./Text";
import { SearchField } from "./Field";
import { SegmentedControl } from "./SegmentedControl";

export interface SelectOption {
  value: string;
  label: string;
  /** Optional compact value shown only in the collapsed trigger. */
  triggerLabel?: string;
  /** Optional trailing hint (e.g. a credit cost). */
  hint?: string;
  shortcut?: string;
  /** Supporting copy in the details layout. */
  description?: string;
  /** Optional leading icon, shown in the trigger and the option row. */
  icon?: ReactNode;
  /** When true the option is shown greyed and can't be selected. */
  disabled?: boolean;
  /**
   * Optional section label. When set, consecutive options sharing a group get a
   * small header above the first of them (e.g. "Image" / "Video"). Keep grouped
   * options contiguous so each group renders once.
   */
  group?: string;
}

export interface SelectFieldProps extends Pick<AriaAttributes, "aria-describedby" | "aria-invalid"> {
  id?: string;
  /** Submit with the surrounding form; required fields retain browser validation. */
  name?: string;
  required?: boolean;
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  /** Compact centered value for toolbars, or a full-width form field with a chevron. */
  size?: "compact" | "regular";
  disabled?: boolean;
  /** Borderless treatments for compact creation controls. */
  variant?: "outline" | "glass" | "secondary" | "ghost";
  /** Extra classes merged onto the trigger button (e.g. a card-style picker). */
  triggerClassName?: string;
  /**
   * Extra classes on the popover root wrapper. The wrapper is `inline-flex` by
   * default (content width); pass `w-full` here to let a `w-full` trigger fill
   * its container.
   */
  className?: string;
  /** Extra classes on the floating option panel. */
  panelClassName?: string;
  context?: ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Compact choices share the same sliding selection used by creation controls. */
  presentation?: "menu" | "segmented";
}

/**
 * Shared single-choice dropdown for forms and composer controls. The visible
 * button owns focus; a hidden native select retains form values and validation.
 */
export const SelectField = forwardRef<HTMLButtonElement, SelectFieldProps>(function SelectField({
  id,
  name,
  required,
  "aria-describedby": describedBy,
  "aria-invalid": invalid,
  label,
  value,
  options,
  onChange,
  placeholder,
  disabled = false,
  size = "compact",
  variant = "outline",
  triggerClassName,
  className,
  panelClassName,
  context,
  searchable = false,
  searchPlaceholder,
  presentation = "menu",
}, ref) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const formControlRef = useRef<HTMLSelectElement>(null);
  const initialValue = useRef(value);
  const validationID = useId();
  const [validationMessage, setValidationMessage] = useState("");
  const isInvalid = invalid === true || invalid === "true" || Boolean(validationMessage);
  useEffect(() => {
    setValidationMessage("");
  }, [value, disabled]);
  useImperativeHandle(ref, () => triggerRef.current!, []);
  useEffect(() => {
    const form = formControlRef.current?.form;
    const reset = () => onChange(initialValue.current);
    form?.addEventListener("reset", reset);
    return () => form?.removeEventListener("reset", reset);
  }, [onChange, name, required]);
  const [optionSearch,setOptionSearch] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const visibleOptions = searchable && optionSearch.trim() ? options.filter(option => `${option.label} ${option.description ?? ""} ${option.group ?? ""}`.toLocaleLowerCase().includes(optionSearch.trim().toLocaleLowerCase())) : options;
  const active = options.find((o) => o.value === value);
  const activeTriggerLabel = active?.triggerLabel ?? active?.label;
  const focusableIndex =
    active && !active.disabled && visibleOptions.includes(active)
      ? visibleOptions.indexOf(active)
      : visibleOptions.findIndex((option) => !option.disabled);
  const defaultActiveValue =
    focusableIndex >= 0 ? (visibleOptions[focusableIndex]?.value ?? null) : null;
  const optionStateKey = JSON.stringify(
    visibleOptions.map((option) => [option.value, !!option.disabled]),
  );
  const [activeOptionValue, setActiveOptionValue] = useState<string | null>(
    defaultActiveValue,
  );

  useEffect(() => {
    setActiveOptionValue(defaultActiveValue);
  }, [defaultActiveValue, optionStateKey]);

  return (
    <>
    <Popover
      label={label}
      side={presentation === "segmented" ? "top" : "bottom"}
      align="start"
      className={cn("min-w-0", size === "regular" && "w-full", className)}
      panelRole={presentation === "segmented" ? "dialog" : null}
      openOnArrowKeys
      onOpenChange={(isOpen) => {
        if (isOpen) { setOptionSearch(""); setActiveOptionValue(defaultActiveValue); }
      }}
      panelClassName={cn(presentation === "segmented" ? "w-[240px] overflow-y-auto p-2" : "studio-select-menu w-[min(216px,calc(100vw-24px))] p-1", panelClassName)}
      trigger={({ open, toggle, panelId }) => (
        <Button
          ref={triggerRef}
          id={id}
          variant={variant}
          data-select-size={size}
          onClick={toggle}
          disabled={disabled}
          aria-haspopup={presentation === "segmented" ? "dialog" : "listbox"}
          aria-expanded={open}
          aria-controls={panelId}
          aria-required={required || undefined}
          aria-describedby={[describedBy, validationMessage && validationID].filter(Boolean).join(" ") || undefined}
          aria-invalid={isInvalid || undefined}
          aria-label={
            active || placeholder
              ? `${label}: ${active?.label ?? placeholder}`
              : label
          }
          className={cn(
            "studio-select-field min-h-8 w-full min-w-0 justify-center gap-1 px-2 py-1 text-[13px] font-semibold",
            isInvalid && "border-danger bg-danger-subtle text-danger-text",
            triggerClassName,
          )}
        >
          <span className="flex min-w-0 items-center justify-center gap-1.5">
            {active?.icon ? (
              <span aria-hidden="true" className="studio-dropdown-icon inline-flex shrink-0">
                {active.icon}
              </span>
            ) : null}
            <span className={cn("min-w-0 truncate text-start", activeTriggerLabel === "" && "sr-only")}>
              {active ? activeTriggerLabel : (placeholder ?? label)}
            </span>
          </span>
          {size === "regular" ? <CaretDownIcon aria-hidden="true" className="studio-select-chevron" /> : null}
        </Button>
      )}
    >
      {({ close, panelId }) => (
        presentation === "segmented" ? <SegmentedField label={label} value={value} options={options} onChange={onChange} disabled={disabled} /> :
        <>
        {context ? <div className="studio-dropdown-context">{context}</div> : null}
        {searchable ? <div className="p-2"><SearchField label={localeText("interface.search_value0_1777397d", { defaultValue: "Search {{value0}}", value0: label.toLocaleLowerCase() })} placeholder={searchPlaceholder ?? localeText("interface.search_value0_14da85fe", { defaultValue: "Search {{value0}}…", value0: label.toLocaleLowerCase() })} value={optionSearch}
          onChange={event => setOptionSearch(event.target.value)} onClear={() => setOptionSearch("")} aria-controls={panelId}
          onKeyDown={event => { if (event.key === "ArrowDown") { event.preventDefault(); listRef.current?.querySelector<HTMLButtonElement>('[role="option"]:not(:disabled)')?.focus(); } }} /></div> : null}
        <div
          id={panelId}
          ref={listRef}
          role="listbox"
          aria-label={label}
          className="flex min-h-0 flex-col overflow-y-auto"
          onKeyDown={moveOptionFocus}
        >
          {visibleOptions.length === 0 ? <p role="status" className="p-3 text-sm text-ink-muted">{localeText("interface.no_matching_options_0f909d16", { defaultValue: "No matching options" })}</p> : null}
          {visibleOptions.map((option, i) => {
            const selected = option.value === value;
            const showHeader =
              !!option.group && option.group !== visibleOptions[i - 1]?.group;
            return (
              <Fragment key={option.value}>
                {showHeader ? (
                  <Text
                    variant="body"
                    as="div"
                    role="presentation"
                    className="studio-dropdown-heading"
                  >
                    {option.group}
                  </Text>
                ) : null}
                <button
                  type="button"
                  role="option"
                  data-context={!!option.description || undefined}
                  aria-selected={selected}
                  tabIndex={option.value === activeOptionValue ? 0 : -1}
                  disabled={option.disabled}
                  onClick={() => {
                    onChange(option.value);
                    close();
                  }}
                  onFocus={() => setActiveOptionValue(option.value)}
                  className={cn(
                    "studio-dropdown-option flex w-full items-center gap-1.5 rounded-sm px-2 text-left outline-none transition-colors duration-150 ease-house hover:bg-canvas-soft focus-visible:bg-canvas-soft focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--focus-ring)] disabled:pointer-events-none disabled:text-ink-faint",
                    selected && "bg-canvas-soft",
                  )}
                >
                  <CheckIcon
                    aria-hidden="true"
                    focusable="false"
                    weight="bold"
                    className={cn(
                      "size-3.5 shrink-0 text-ink",
                      selected ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {option.icon ? (
                    <span aria-hidden="true" className="studio-dropdown-icon inline-flex shrink-0">
                      {option.icon}
                    </span>
                  ) : null}
                  <span className="min-w-0 flex-1 break-words font-sans text-[13px] font-semibold">
                    {option.label}
                    {option.description ? <span className="studio-dropdown-description">{option.description}</span> : null}
                  </span>
                  {option.shortcut ? <kbd className="studio-shortcut">{option.shortcut}</kbd> : null}
                  {option.hint ? (
                    <Text
                      variant="body"
                      as="span"
                      className="shrink-0 text-[11.5px] text-ink-faint"
                    >
                      {option.hint}
                    </Text>
                  ) : null}
                </button>
              </Fragment>
            );
          })}
        </div>
        </>
      )}
    </Popover>
    {name || required ? (
      <select
        ref={formControlRef}
        name={name}
        value={value}
        required={required}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none absolute size-px opacity-0"
        onChange={(event) => onChange(event.currentTarget.value)}
        onInvalid={(event) => {
          event.preventDefault();
          setValidationMessage(event.currentTarget.validationMessage || localeText("interface.choose_an_option_3956790a", { defaultValue: "Choose an option." }));
          const firstInvalid = Array.from(event.currentTarget.form?.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("input, select, textarea") ?? [])
            .find((control) => control.willValidate && !control.validity.valid);
          if (!firstInvalid || firstInvalid === event.currentTarget) triggerRef.current?.focus();
        }}
      >
        {!options.some((option) => option.value === "") ? <option value="" /> : null}
        {options.map((option) => <option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</option>)}
      </select>
    ) : null}
    {validationMessage ? <p id={validationID} role="alert" className="mt-1.5 text-xs text-danger-text">{validationMessage}</p> : null}
    </>
  );
});

/** A labeled sliding selector, also usable inside an existing settings popover. */
export function SegmentedField({ label, value, options, onChange, disabled }: Pick<SelectFieldProps, "label" | "value" | "options" | "onChange" | "disabled">) {
  const minWidth = Math.max(0, ...options.map(option => option.label.length * 8 + 24)) * options.length + 8;
  return <div className="grid min-w-0 gap-1.5">
    <Text as="span" variant="ui" className="px-1 text-xs text-ink-muted">{label}</Text>
    <div className="overflow-x-auto" onFocusCapture={event => event.target.scrollIntoView?.({ block: "nearest", inline: "nearest" })}>
      <div style={{ minWidth }}>
        <SegmentedControl aria-label={label} value={value} options={options.map(({ value, label, disabled }) => ({ value, label, disabled }))} onChange={onChange} disabled={disabled} size="sm" fullWidth />
      </div>
    </div>
  </div>;
}

function moveOptionFocus(event: KeyboardEvent<HTMLDivElement>): void {
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
  event.preventDefault();
  const options = Array.from(
    event.currentTarget.querySelectorAll<HTMLButtonElement>(
      '[role="option"]:not(:disabled)',
    ),
  );
  if (!options.length) return;
  const current = options.indexOf(document.activeElement as HTMLButtonElement);
  const next =
    event.key === "Home"
      ? options[0]
      : event.key === "End"
        ? options.at(-1)
        : options[
            (current + (event.key === "ArrowDown" ? 1 : -1) + options.length) %
              options.length
          ];
  next?.focus();
}
